<template>
    <Page class="index">
        <div class="hero py-6 py-md-10 py-lg-10">
            <v-container class="px-4 px-md-0">
                <v-row no-gutters>
                    <v-col cols="12" md="8">
                        <p class="header mb-10">
                            Enable system observability in minutes, no code changes required
                        </p>
                        <p class="message mb-10">
                            Metrics, logs, traces, continuous profiling, and SLO-based alerting, supercharged with predefined dashboards and inspections.
                        </p>
                        <div class="d-sm-flex">
                            <router-link :to="{name: 'cloud'}" class="filled-blue d-block d-sm-inline text-center mr-0 mr-sm-5">Try Coroot Cloud for free</router-link>
                            <a href="https://community-demo.coroot.com/" target="_blank" class="outlined d-block d-sm-inline text-center mt-5 mt-sm-0">Live demo</a>
                        </div>
                        <div class="mt-4 ml-2">
                            <router-link class="link" :to="{name: 'docs', params: {chapter: 'coroot-community-edition', group: 'getting-started', item: 'installation'}}">
                                or install open-source Coroot &rarr;
                            </router-link>
                        </div>
                    </v-col>
                    <v-col cols="12" md="4" class="d-none d-md-block">
                        <v-img contain height="300" position="left center" src="/static/img/hero.svg"></v-img>
                    </v-col>
                </v-row>
            </v-container>
        </div>

        <div class="features pb-10 pb-md-12 pb-lg-15">
            <p class="header">
                Zero-instrumentation observability
            </p>
            <v-row>
                <v-col cols="12" md="8">
                    <v-card class="pa-3">
                        <ImageWrapper src="/static/img/service_map.png" />
                    </v-card>
                </v-col>
                <v-col cols="12" md="4">
                    <div class="feature">
                        <div>
                            <v-icon class="icon">mdi-check-circle</v-icon>
                        </div>
                        <div>
                            Metrics, logs, traces, and profiles are gathered automatically by using eBPF
                        </div>
                    </div>
                    <div class="feature">
                        <div>
                            <v-icon class="icon">mdi-check-circle</v-icon>
                        </div>
                        <div>
                            Coroot provides you with a Service Map that covers 100% of your system with no blind spots
                        </div>
                    </div>
                    <div class="feature">
                        <div>
                            <v-icon class="icon">mdi-check-circle</v-icon>
                        </div>
                        <div>
                            Predefined inspections audit each application without any configuration
                        </div>
                    </div>
                </v-col>
            </v-row>

            <p class="header">
                Application Health Summary
            </p>
            <v-row>
                <v-col cols="12" md="4">
                    <div class="feature">
                        <div>
                            <v-icon class="icon">mdi-check-circle</v-icon>
                        </div>
                        <div>
                            Easily understand the status of your services, even when dealing with hundreds of them
                        </div>
                    </div>
                    <div class="feature">
                        <div>
                            <v-icon class="icon">mdi-check-circle</v-icon>
                        </div>
                        <div>
                            Gain insight into application logs without the need to manually inspect each one
                        </div>
                    </div>
                    <div class="feature">
                        <div>
                            <v-icon class="icon">mdi-check-circle</v-icon>
                        </div>
                        <div>
                            SLOs (Service Level Objectives) tracking
                        </div>
                    </div>
                </v-col>
                <v-col cols="12" md="8">
                    <v-card class="pa-3">
                        <ImageWrapper src="/static/img/app_health_summary.png" />
                    </v-card>
                </v-col>
            </v-row>


            <p class="header">
                Explore any outlier requests with distributed tracing
            </p>
            <v-row>
                <v-col cols="12" md="8">
                    <v-card class="pa-3">
                        <ImageWrapper src="/static/img/tracing_demo.gif" />
                    </v-card>
                </v-col>
                <v-col cols="12" md="4">
                    <div class="feature">
                        <div>
                            <v-icon class="icon">mdi-check-circle</v-icon>
                        </div>
                        <div>
                            Investigate any anomaly with just one click
                        </div>
                    </div>
                    <div class="feature">
                        <div>
                            <v-icon class="icon">mdi-check-circle</v-icon>
                        </div>
                        <div>
                            Vendor-neutral instrumentation with OpenTelemetry
                        </div>
                    </div>
                    <div class="feature">
                        <div>
                            <v-icon class="icon">mdi-check-circle</v-icon>
                        </div>
                        <div>
                            Are you unable to instrument legacy or third-party services?<br>
                            Coroot's eBPF-based instrumentation can capture requests without requiring any code changes.
                        </div>
                    </div>
                </v-col>
            </v-row>


            <p class="header">
                Grasp insights from logs with just a quick glance
            </p>
            <v-row>
                <v-col cols="12" md="4">
                    <div class="feature">
                        <div>
                            <v-icon class="icon">mdi-check-circle</v-icon>
                        </div>
                        <div>
                            Log patterns: out-of-the-box event clustering
                        </div>
                    </div>
                    <div class="feature">
                        <div>
                            <v-icon class="icon">mdi-check-circle</v-icon>
                        </div>
                        <div>
                            Seamless logs-to-traces correlation
                        </div>
                    </div>
                    <div class="feature">
                        <div>
                            <v-icon class="icon">mdi-check-circle</v-icon>
                        </div>
                        <div>
                            Lightning-fast search based on ClickHouse
                        </div>
                    </div>
                </v-col>
                <v-col cols="12" md="8">
                    <v-card class="pa-3">
                        <ImageWrapper src="/static/img/log_monitoring.png" />
                    </v-card>
                </v-col>
            </v-row>

            <p class="header">
                Profile any application in 1 click
            </p>
            <v-row>
                <v-col cols="12" md="8">
                    <v-card class="pa-3">
                        <ImageWrapper src="/static/img/pyroscope_comparison.png" />
                    </v-card>
                </v-col>
                <v-col cols="12" md="4">
                    <div class="feature">
                        <div>
                            <v-icon class="icon">mdi-check-circle</v-icon>
                        </div>
                        <div>
                            Analyze any unexpected spike in CPU or memory usage down to the precise line of code
                        </div>
                    </div>
                    <div class="feature">
                        <div>
                            <v-icon class="icon">mdi-check-circle</v-icon>
                        </div>
                        <div>
                            Don't make assumptions, know exactly what the resources were spent on
                        </div>
                    </div>
                    <div class="feature">
                        <div>
                            <v-icon class="icon">mdi-check-circle</v-icon>
                        </div>
                        <div>
                            Easily investigate any anomaly by comparing it to the system's baseline behavior
                        </div>
                    </div>
                </v-col>
            </v-row>


            <p class="header">
                AI-powered root cause analysis
            </p>

            <v-row>
                <v-col cols="12" md="4">
                    <div class="feature">
                        <div>
                            <v-icon class="icon">mdi-check-circle</v-icon>
                        </div>
                        <div>
                            Explain any anomalies in seconds
                        </div>
                    </div>
                    <div class="feature">
                        <div>
                            <v-icon class="icon">mdi-check-circle</v-icon>
                        </div>
                        <div>
                            Coroot has all the necessary telemetry data to precisely pinpoint the root cause
                        </div>
                    </div>
                    <div class="feature">
                        <div>
                            <v-icon class="icon">mdi-check-circle</v-icon>
                        </div>
                        <div>
                            Provides all the evidence, allowing you to crosscheck easily
                        </div>
                    </div>
                    <div class="feature">
                        <div></div>
                        <div>
                            <sup>*</sup> available only in <a href="/cloud">Coroot Cloud</a>
                        </div>
                    </div>
                </v-col>
                <v-col cols="12" md="8">
                    <v-card class="pa-3">
                        <ImageWrapper src="/static/img/rca.png" />
                    </v-card>
                </v-col>
            </v-row>


            <p class="header">
                Built-in expertise
            </p>
            <v-row>
                <v-col cols="12" md="8">
                    <v-card class="pa-4">
                        <ImageWrapper src="/static/img/slack_alert.png" />
                    </v-card>
                </v-col>
                <v-col cols="12" md="4">
                    <div class="feature">
                        <div>
                            <v-icon class="icon">mdi-check-circle</v-icon>
                        </div>
                        <div>
                            Coroot can automatically identify over 80% of issues
                        </div>
                    </div>
                    <div class="feature">
                        <div>
                            <v-icon class="icon">mdi-check-circle</v-icon>
                        </div>
                        <div>
                            If an app is not meeting its Service Level Objectives (SLOs), Coroot will send a single alert that includes
                            the results of all relevant inspections
                        </div>
                    </div>
                    <div class="feature">
                        <div>
                            <v-icon class="icon">mdi-check-circle</v-icon>
                        </div>
                        <div>
                            You can easily adjust any inspection for a particular application or an entire project
                        </div>
                    </div>
                </v-col>
            </v-row>


            <p class="header">
                Deployment Tracking
            </p>
            <v-row>
                <v-col cols="12" md="4">
                    <div class="feature">
                        <div>
                            <v-icon class="icon">mdi-check-circle</v-icon>
                        </div>
                        <div>
                            Coroot discovers and monitors every application rollout in your Kubernetes cluster
                        </div>
                    </div>
                    <div class="feature">
                        <div>
                            <v-icon class="icon">mdi-check-circle</v-icon>
                        </div>
                        <div>
                            Requires no integration with your CI/CD pipeline
                        </div>
                    </div>
                    <div class="feature">
                        <div>
                            <v-icon class="icon">mdi-check-circle</v-icon>
                        </div>
                        <div>
                            Each release is automatically compared with the previous one, so you'll never miss even the slightest performance degradation
                        </div>
                    </div>
                    <div class="feature">
                        <div>
                            <v-icon class="icon">mdi-check-circle</v-icon>
                        </div>
                        <div>
                            With integrated Cost Monitoring, developers can track how each change affects their cloud bill
                        </div>
                    </div>
                </v-col>
                <v-col cols="12" md="8">
                    <v-card class="pa-4">
                        <ImageWrapper src="/static/img/peacetime_observability.png" />
                    </v-card>
                </v-col>
            </v-row>

            <p class="header">
                Cost Monitoring
            </p>
            <v-row>
                <v-col cols="12" md="8">
                    <v-card class="pa-4">
                        <ImageWrapper src="/static/img/cost_monitoring.png" />
                    </v-card>
                </v-col>
                <v-col cols="12" md="4">
                    <div class="feature">
                        <div>
                            <v-icon class="icon">mdi-check-circle</v-icon>
                        </div>
                        <div>
                            Understand your cloud costs down to any given application
                        </div>
                    </div>
                    <div class="feature">
                        <div>
                            <v-icon class="icon">mdi-check-circle</v-icon>
                        </div>
                        <div>
                            Doesn't require access to you cloud account or any other configurations
                        </div>
                    </div>
                    <div class="feature">
                        <div>
                            <v-icon class="icon">mdi-check-circle</v-icon>
                        </div>
                        <div>
                            AWS, GCP, Azure
                        </div>
                    </div>
                </v-col>
            </v-row>

            <div>
                <v-row class="mt-16">
                    <v-col><img class="ma-auto d-block" height="40px" src="/static/img/logos/johndeer.png" /></v-col>
                    <v-col><img class="ma-auto d-block" height="40px" src="/static/img/logos/indrive.png" /></v-col>
                    <v-col><img class="ma-auto d-block" height="40px" src="/static/img/logos/squarespace.png" /></v-col>
                    <v-col><img class="ma-auto d-block" height="40px" src="/static/img/logos/zapier.png" /></v-col>
                    <v-col><img class="ma-auto d-block" height="40px" src="/static/img/logos/pecan.png" /></v-col>
                    <v-col><img class="ma-auto d-block" height="40px" src="/static/img/logos/cyware.png" /></v-col>
                </v-row>
            </div>

            <v-row class="stats">
                <v-col class="item">
                    <div class="indicator">4M+</div>
                    <div class="name">DOWNLOADS</div>
                </v-col>
                <v-col class="item">
                    <div class="indicator">4.6K</div>
                    <div class="name">STARS</div>
                </v-col>
                <v-col class="item">
                    <div class="indicator">150+</div>
                    <div class="name">FORKS</div>
                </v-col>
            </v-row>

            <Banner />

        </div>
    </Page>
</template>

<script>
import Banner from "@/components/Banner";
import Page from "@/pages/Page";

export default {
    components: {Page, Banner},
};
</script>

<style scoped>
.index {
    font-family: Poppins, sans-serif;
}
.hero {
    background-color: #080b1d;
    color: white;
    margin-left: calc(50% - 50vw);
    margin-right: calc(50% - 50vw);
}
.hero .header {
    font-size: 30px;
    font-weight: 500;
    line-height: 1.5;
    opacity: 0.9;
}
.hero .message {
    font-size: 18px;
    font-weight: 400;
    line-height: 1.5;
    opacity: 0.8;
}
.hero a {
    font-weight: 500;
    font-size: 18px;
    white-space: nowrap;
}

.hero .link  {
    font-size: 16px;
    color: white !important;
    opacity: 0.8;
}

.features {
    /*padding: 30px 0;*/
}
.features > .header {
    font-size: 24px;
    line-height: 1.5;
    font-weight: 600;
    text-align: center;
    margin: 60px 0 24px 0;
}
.feature {
    display: flex;
    gap: 16px;
    align-items: center;
    margin-bottom: 16px;
}
.feature .icon {
    color: #4BB649;
    font-size: 20px;
}



.stats {
    margin: 40px 0;
}
.stats .item {
    text-align: center;
}
.stats .item:not(:last-child) {
    border-right: 1px solid rgba(0,0,0,0.12);
}

.stats .item .indicator {
    color: #4BB649;
    font-weight: 600;
    font-size: 36px;
    line-height: 1.1;
}
.stats .item .name {
    font-size: 14px;
}

@media (min-width: 600px) {
    .hero .header {
        font-size: 36px;
    }
    .hero .message {
        font-size: 22px;
    }
    .features > .header {
        font-size: 30px;
        margin: 80px 0 32px 0;
    }
    .stats {
        margin: 60px 0;
    }
    .stats .item .indicator {
        font-size: 48px;
    }
    .stats .item .name {
        font-size: 18px;
    }
}
@media (min-width: 960px) {
    .hero .header {
        font-size: 48px;
    }
    .hero .message {
        font-size: 24px;
    }
    .features > .header {
        font-size: 36px;
        margin: 100px 0 40px 0;
    }
    .stats {
        margin: 80px 0;
    }
    .stats .item .indicator {
        font-size: 60px;
    }
    .stats .item .name {
        font-size: 20px;
    }
}

.values .header {
    font-size: 24px;
    font-weight: 600;
    line-height: 32px;
    flex-grow: 1;
    margin-bottom: 0;
}
.values .message {
    font-size: 16px;
}

</style>
